import moment from 'moment';
import React, { useMemo } from 'react';

import styled from '@emotion/styled';
import CircleIcon from '@mui/icons-material/Circle';
import { Box, Button, TextField, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import { DeviceHealthData } from 'modules/Telematics';
import Timeline from 'modules/Telematics/components/TelematicsRightPanel/Timeline';
import {
	PARKING_COLOR,
	POLYGON_COLOR,
	STANDING_EVENT_COLOR,
} from 'modules/Telematics/constants';
import {
	AllSegmentTypes,
	TimelineDataInterface,
} from 'modules/Telematics/types';
import DateRangePicker from 'widgets/DateRangePicker';
import AccordionView from './AccordionView';
import LiveSummary from './LiveSummary';

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	keepMounted?: boolean;
	value: number;
}

function TabPanel(props: TabPanelProps) {
	const { children, value, index, keepMounted = false, ...other } = props;
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{(keepMounted || value === index) && (
				<Box
					sx={{ p: 3 }}
					style={{ display: value === index ? 'block' : 'none' }}
				>
					<Box>{children}</Box>
				</Box>
			)}
		</div>
	);
}

function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
	};
}

function DeviceHealthLabel({ color }: { color: string }) {
	return (
		<Box display="flex" alignItems="center">
			<span>Device Health</span>
			<span
				style={{
					marginLeft: '2px',
				}}
			>
				<CircleIcon
					style={{
						color,
					}}
				/>
			</span>
		</Box>
	);
}

function pad(num: number) {
	return ('0' + num).slice(-2);
}

function hhmmss(secs: number) {
	let minutes = Math.floor(secs / 60);
	secs %= 60;
	let hours = Math.floor(minutes / 60);
	minutes %= 60;
	return `${pad(hours)} Hrs ${pad(minutes)} mins ${pad(secs)} secs`;
}

const TelematicsRightPanel = ({
	applyFilters,
	startDate,
	endDate,
	setStartDateValue,
	setEndDateValue,
	machineInputValue,
	onMachineIdChange,
	machineIdList,
	machinePolygonData,
	onClickTimeSegment,
	isRawDataShown,
	handleLiveDataChange,
	deviceHealthData,
	timelineData,
	totalBookings,
}: {
	applyFilters: any;
	startDate: any;
	endDate: any;
	setStartDateValue: any;
	setEndDateValue: any;
	machineInputValue: any;
	onMachineIdChange: any;
	machineIdList: any;
	machinePolygonData: any;
	onClickTimeSegment: any;
	isRawDataShown: any;
	handleLiveDataChange: any;
	deviceHealthData: DeviceHealthData | null;
	timelineData: TimelineDataInterface[];
	totalBookings: number;
}) => {
	const [activeTabIndex, setActiveTabIndex] = React.useState(0);
	const sortedTimelineData = timelineData.sort((row1, row2) => {
		return row1.startTime - row2.startTime;
	});
	const [coveragePercentage, totalDistance] = useMemo(() => {
		let polypath = 0;
		let drypath = 0;
		sortedTimelineData.forEach((element) => {
			switch (element.metadata.type) {
				case AllSegmentTypes.POLY_SEGMENT:
					polypath += element.tagData
						.DISTANCE_TRAVELLED_METERS as number;
					break;
				case AllSegmentTypes.DRY_RUN_SEGMENT:
					drypath += element.tagData
						.DISTANCE_TRAVELLED_METERS as number;
					break;
				default:
			}
		});
		if (polypath + drypath > 0) {
			return [
				(polypath / (polypath + drypath)) * 100,
				((polypath + drypath) / 1000).toFixed(1),
			];
		}
		return [0, ((polypath + drypath) / 1000).toFixed(1)];
	}, [sortedTimelineData]);

	const deviceHealthTableData = useMemo(() => {
		const primaryCols = ['Machine No.', 'Time', 'Device status'];
		const secondaryCols = [
			'id',
			'Average distance.',
			'Average freq',
			'Device Id',
			'Time since last ping',
			'Seq No.',
		];
		let rows: Array<{ [key: string]: string | number }> = [];
		if (deviceHealthData) {
			if (deviceHealthData.isMalfunctioned) {
				rows = deviceHealthData.deviceStatus.map((instance) => {
					const startTime = moment(
						instance.timeRange.startTime.seconds * 1000,
					).format('DD MMM YYYY hh:mm A');
					const endTime = moment(
						instance.timeRange.endTime.seconds * 1000,
					).format('DD MMM YYYY hh:mm A');
					return {
						id: instance.uuid,
						Time: startTime + ' - ' + endTime,
						'Average distance.': instance.averageDistance,
						'Average freq': instance.averageFrequency,
						'Device Id': instance.deviceId,
						'Device status': instance.iotDeviceStatus,
						'Machine No.': instance.machineNo,
						'Time since last ping': hhmmss(
							instance.millisSinceLastPing / 1000,
						),
						'Seq No.': instance.seqNo,
					};
				});
			}
		}
		return { primaryCols, secondaryCols, rows };
	}, [deviceHealthData]);

	const computeTotalAcres = () => {
		return machinePolygonData.reduce(
			(acc: any, { polygonAcre }: { polygonAcre: any }) =>
				parseFloat(acc) + parseFloat(polygonAcre),
			0,
		);
	};
	const updateActiveTabIndex = (
		event: React.SyntheticEvent,
		newValue: number,
	) => {
		setActiveTabIndex(newValue);
	};

	return (
		<Box>
			<Typography sx={{ fontSize: '20px' }}>
				Machine Spray Telematics
			</Typography>
			<DateRangePicker
				startDate={startDate}
				endDate={endDate}
				setStartDateValue={setStartDateValue}
				setEndDateValue={setEndDateValue}
			/>
			<Box sx={{ marginBottom: '30px' }}>
				<Box display="flex">
					<Autocomplete
						sx={{
							marginRight: '26px',
							marginTop: '20px',
							verticalAlign: 'center',
							width: 300,
						}}
						disablePortal
						id="machine-id-new"
						options={machineIdList}
						value={machineInputValue}
						onChange={(e, value) => onMachineIdChange(value)}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Machine Number"
								placeholder="Search for asset"
							/>
						)}
					/>
					<FormGroup sx={{ marginTop: '25px' }}>
						<FormControlLabel
							control={
								<Checkbox
									checked={isRawDataShown}
									onChange={handleLiveDataChange}
								/>
							}
							label="Raw Data"
						/>
					</FormGroup>
				</Box>
				<Button
					onClick={applyFilters}
					size="medium"
					sx={{
						width: '30%',
						marginTop: '20px',
						':hover': {
							scale: '1.01',
						},
					}}
					variant="contained"
					disabled={!machineInputValue}
				>
					Filter
				</Button>
			</Box>
			<Box sx={{ width: '100%' }}>
				<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
					<Tabs
						value={activeTabIndex}
						onChange={updateActiveTabIndex}
						aria-label="basic tabs example"
					>
						<Tab label="Trip History" {...a11yProps(0)} />
						<Tab
							label={
								<DeviceHealthLabel
									color={
										deviceHealthData?.isMalfunctioned
											? 'red'
											: 'green'
									}
								/>
							}
							{...a11yProps(1)}
						/>
						<Tab label="Live Summary" {...a11yProps(2)} />
					</Tabs>
				</Box>
				<TabPanel value={activeTabIndex} index={0}>
					<Box>
						<Box>
							<StyledText sx={{ color: 'rgba(0,0,0,0.6)' }}>
								Polygons
							</StyledText>
							<StyledText
								sx={{ fontWeight: 600, marginLeft: '10px' }}
							>{`${machinePolygonData?.length || 0}`}</StyledText>
							<StyledText
								sx={{
									marginLeft: '20px',
									color: 'rgba(0,0,0,0.6)',
								}}
							>
								Acres
							</StyledText>
							<StyledText
								sx={{ fontWeight: 600, marginLeft: '10px' }}
							>
								{computeTotalAcres()?.toFixed(0) || 0} (
								{Math.ceil(coveragePercentage)}%)
							</StyledText>
						</Box>
						<Box>
							<StyledText
								sx={{
									color: 'rgba(0,0,0,0.6)',
								}}
							>
								Distance
							</StyledText>
							<StyledText
								sx={{ fontWeight: 600, marginLeft: '10px' }}
							>
								{totalDistance} kms
							</StyledText>
							<StyledText
								sx={{
									marginLeft: '20px',
									color: 'rgba(0,0,0,0.6)',
								}}
							>
								Bookings
							</StyledText>
							<StyledText
								sx={{ fontWeight: 600, marginLeft: '10px' }}
							>
								{totalBookings}
							</StyledText>
						</Box>
						<Box
							sx={{
								marginTop: '20px',
								display: 'flex',
								flexWrap: 'wrap',
								alignItems: 'center',
							}}
						>
							<Typography marginRight="5px" fontSize="12px">
								Dry Path
							</Typography>
							<Box
								sx={{
									border: '1px solid black',
									height: '15px',
									width: '15px',
									background: 'yellow',
								}}
							></Box>
							<Typography
								marginLeft="10px"
								fontSize="12px"
								marginRight="5px"
							>
								Polygon
							</Typography>
							<Box
								sx={{
									border: '1px solid black',
									height: '15px',
									width: '15px',
									background: POLYGON_COLOR,
								}}
							></Box>
							<Typography
								marginLeft="10px"
								fontSize="12px"
								marginRight="5px"
							>
								Standing Event
							</Typography>
							<Box
								sx={{
									border: '1px solid black',
									height: '15px',
									width: '15px',
									background: STANDING_EVENT_COLOR,
								}}
							></Box>
						</Box>
						<Box
							sx={{
								marginTop: '10px',
								display: 'flex',
								flexWrap: 'wrap',
								alignItems: 'center',
							}}
						>
							<Typography fontSize="12px" marginRight="5px">
								Parking Event
							</Typography>
							<Box
								sx={{
									border: '1px solid black',
									height: '15px',
									width: '15px',
									background: PARKING_COLOR,
								}}
							></Box>
							<Typography
								marginLeft="10px"
								fontSize="12px"
								marginRight="5px"
							>
								Malicious Segment
							</Typography>
							<Box
								sx={{
									border: '1px solid black',
									height: '15px',
									width: '15px',
									background: 'grey',
								}}
							></Box>
						</Box>
						<Timeline
							onClickTimeSegment={onClickTimeSegment}
							combinedData={sortedTimelineData}
						/>
					</Box>
				</TabPanel>
				<TabPanel value={activeTabIndex} index={1}>
					<Box>
						{deviceHealthData?.isMalfunctioned ? (
							deviceHealthTableData.rows.map(
								(row: any, index: any) => {
									return (
										<AccordionView
											key={index}
											uuid={index}
											background=""
											row={row}
											primaryCols={
												deviceHealthTableData.primaryCols
											}
											secondaryCols={
												deviceHealthTableData.secondaryCols
											}
										/>
									);
								},
							)
						) : (
							<div>No instances of malfunctioning</div>
						)}
					</Box>
				</TabPanel>
				<TabPanel value={activeTabIndex} index={2}>
					<Box>
						<LiveSummary />
					</Box>
				</TabPanel>
			</Box>
		</Box>
	);
};

const StyledText = styled(Typography)`
	font-size: 14px;
	display: inline-block;
`;

export default TelematicsRightPanel;
